import React, { type ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import SearchIcon from '@mui/icons-material/Search'
import { Box, CircularProgress, IconButton, Typography } from '@mui/material'

import { ChevronDownIcon } from '~assets/icons'
import { Icon } from '~components/icons'
import { Button, InputLabelStyled } from '~components/internal'
import { type ELVoice } from '~types'

type VoiceSelectorElevenLabsProps = {
  onSelect?: (voice: ELVoice | null) => void
  placeholder?: string
  selectedVoiceId?: string
  voices: ELVoice[]
  isPreviewLoading: boolean
  isPreviewPlaying: boolean
  onPreviewGreeting: () => void
  greetingMessage: string
}

const VoiceSelectorElevenLabs = ({
  onSelect,
  placeholder = 'Select a voice',
  selectedVoiceId,
  voices,
  isPreviewLoading,
  isPreviewPlaying,
  onPreviewGreeting,
  greetingMessage,
}: VoiceSelectorElevenLabsProps): ReactElement => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownListRef = useRef<HTMLDivElement>(null)
  const voiceAudioRef = useRef<HTMLAudioElement | null>(null)
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)

  const filteredVoices = voices.filter((voice) =>
    voice.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const selectedVoice = voices.find((voice) => voice.voice_id === selectedVoiceId)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node
      const isClickInOriginal = dropdownRef.current && dropdownRef.current.contains(target)
      const isClickInDropdown = dropdownListRef.current && dropdownListRef.current.contains(target)
      if (!isClickInOriginal && !isClickInDropdown) setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return (): void => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const stopVoiceAudio = useCallback((): void => {
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause()
      voiceAudioRef.current.src = ''
      voiceAudioRef.current = null
    }
    setPlayingVoiceId(null)
  }, [])

  useEffect(() => {
    if (isOpen) {
      const handleScroll = (e: Event): void => {
        if (dropdownListRef.current && dropdownListRef.current.contains(e.target as Node)) return
        setIsOpen(false)
      }
      window.addEventListener('scroll', handleScroll, true)
      return (): void => window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) stopVoiceAudio()
    return (): void => {
      stopVoiceAudio()
    }
  }, [isOpen, stopVoiceAudio])

  const handleToggle = (): void => setIsOpen((prev) => !prev)

  const handleOptionClick = (voice: ELVoice): void => {
    setIsOpen(false)
    onSelect?.(voice)
  }

  const handleClearSelection = (e: React.MouseEvent): void => {
    e.stopPropagation()
    onSelect?.(null)
  }

  const handlePreviewClick = (e: React.MouseEvent, voice: ELVoice): void => {
    e.stopPropagation()
    stopVoiceAudio()
    if (!voice.preview_url || playingVoiceId === voice.voice_id) return

    const audio = new Audio(voice.preview_url)
    voiceAudioRef.current = audio
    setPlayingVoiceId(voice.voice_id)
    audio.addEventListener('ended', () => {
      if (voiceAudioRef.current === audio) {
        setPlayingVoiceId(null)
        voiceAudioRef.current = null
      }
    })
    audio.addEventListener('error', () => {
      if (voiceAudioRef.current === audio) {
        setPlayingVoiceId(null)
        voiceAudioRef.current = null
      }
    })
    audio.play()
  }

  return (
    <Box sx={{ width: '100%' }}>
      <InputLabelStyled shrink>Voice</InputLabelStyled>

      {/* Selector + preview button row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <div
          className={`org-selector ${isOpen ? 'active' : ''}`}
          ref={dropdownRef}
          style={{ flex: 1, minWidth: 0 }}
        >
          <div
            className='org-selector-header'
            onClick={handleToggle}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: 'white',
              minHeight: '40px',
              boxSizing: 'border-box',
            }}
          >
            <RecordVoiceOverIcon sx={{ width: 20, height: 20, color: '#666', mr: 1 }} />
            <p
              style={{
                margin: 0,
                flex: 1,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontSize: '14px',
                color: selectedVoice ? 'inherit' : '#666',
              }}
            >
              {selectedVoice ? selectedVoice.name : placeholder}
            </p>
            {selectedVoice && (
              <span
                onClick={handleClearSelection}
                style={{
                  cursor: 'pointer',
                  color: '#666',
                  fontSize: '20px',
                  padding: '0 8px',
                  lineHeight: 1,
                }}
              >
                ×
              </span>
            )}
            <div style={{ display: 'flex', alignItems: 'center' }} className='dropdown-arrow'>
              <ChevronDownIcon width={12} />
            </div>
          </div>

          {isOpen &&
            ReactDOM.createPortal(
              <div
                ref={dropdownListRef}
                className='org-selector-list'
                style={{
                  position: 'fixed',
                  zIndex: 1500,
                  backgroundColor: 'white',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                  borderRadius: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  width: dropdownRef.current ? `${dropdownRef.current.offsetWidth}px` : '250px',
                  top: dropdownRef.current
                    ? `${dropdownRef.current.getBoundingClientRect().bottom}px`
                    : '0',
                  left: dropdownRef.current
                    ? `${dropdownRef.current.getBoundingClientRect().left}px`
                    : '0',
                }}
              >
                <Box
                  sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      py: 0.5,
                      border: '1px solid rgba(0,0,0,0.23)',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                    <input
                      value={searchTerm}
                      onChange={(e) => {
                        e.stopPropagation()
                        setSearchTerm(e.target.value)
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder='Search voices...'
                      autoFocus
                      style={{
                        border: 'none',
                        outline: 'none',
                        fontSize: 14,
                        flex: 1,
                        background: 'transparent',
                        padding: '2px 0',
                      }}
                    />
                  </Box>
                </Box>
                {filteredVoices.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      No voices found
                    </Typography>
                  </Box>
                ) : (
                  filteredVoices.map((voice) => (
                    <div
                      key={voice.voice_id}
                      className='org-selector-item'
                      onClick={() => handleOptionClick(voice)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        cursor: 'pointer',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {voice.name}
                        </Typography>
                        {voice.description && (
                          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                            {voice.description}
                          </Typography>
                        )}
                      </Box>
                      {voice.preview_url && (
                        <IconButton size='small' onClick={(e) => handlePreviewClick(e, voice)}>
                          {playingVoiceId === voice.voice_id ? (
                            <Icon name='Pause' fontSize='small' />
                          ) : (
                            <Icon name='PlayArrow' fontSize='small' />
                          )}
                        </IconButton>
                      )}
                    </div>
                  ))
                )}
              </div>,
              document.body,
            )}
        </div>

        {/* Preview greeting button — right-aligned, inline with dropdown */}
        <Button
          size='small'
          variant='text'
          startIcon={
            isPreviewLoading ? (
              <CircularProgress size={16} color='inherit' />
            ) : isPreviewPlaying ? (
              <Icon name='pause' />
            ) : (
              <Icon name='play_arrow' />
            )
          }
          onClick={onPreviewGreeting}
          disabled={!selectedVoiceId || !greetingMessage.trim() || isPreviewLoading}
          sx={{
            flexShrink: 0,
            whiteSpace: 'nowrap',
            minWidth: '160px',
            justifyContent: 'flex-start',
          }}
        >
          {isPreviewLoading
            ? 'Loading preview'
            : isPreviewPlaying
              ? 'Stop preview'
              : 'Preview greeting'}
        </Button>
      </Box>
    </Box>
  )
}

export default VoiceSelectorElevenLabs
